import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Check, CheckCircle2, Sparkles, Terminal } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, ApiError } from "@/lib/api";
import { relativeTime } from "@/lib/format";
import { qk } from "@/lib/query/keys";
import { cn } from "@/lib/utils";
import type { EngineModelOption, EngineSettings, EngineType } from "@/lib/types";

const select = (r: { settings: EngineSettings }) => r.settings;

type Patch = { activeEngine?: EngineType; agyModel?: string; claudeModel?: string };

/**
 * Settings → AI engine: Claude Code or the Antigravity CLI for agents' conversations,
 * and a default model for each. Whether agy can be chosen is the worker's reading of
 * the host (installed, signed in), so the tile says why when it cannot.
 */
export function EngineSettingsPanel() {
  const client = useQueryClient();
  const query = useQuery({ queryKey: qk.engineSettings(), queryFn: () => api.engineSettings(), select, refetchInterval: 15_000 });
  const mutation = useMutation({
    mutationFn: (patch: Patch) => api.setEngineSettings(patch),
    onSuccess: (result) => client.setQueryData(qk.engineSettings(), result),
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Could not save the engine settings"),
  });

  if (query.isPending) return <p className="text-[13px] text-muted-foreground">Loading…</p>;
  const settings = query.data;
  if (!settings) return <p className="text-[13px] text-muted-foreground">Could not load the engine settings.</p>;
  const active = settings.activeEngine;
  const choose = (engine: EngineType) => {
    if (engine !== active) mutation.mutate({ activeEngine: engine }, { onSuccess: () => toast.success(engine === "agy" ? "Agents now run on Antigravity" : "Agents now run on Claude Code") });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-2.5 win-sm:grid-cols-2">
        <EngineTile
          icon={<Terminal className="size-4" />}
          title="Claude Code"
          detail={<><code className="font-mono">claude -p</code>, signed in as the Claude account below.</>}
          active={active === "claude"}
          disabled={mutation.isPending}
          onPick={() => choose("claude")}
        />
        <EngineTile
          icon={<Sparkles className="size-4" />}
          title="Antigravity"
          detail={<>Google's <code className="font-mono">agy</code> CLI: Gemini and other models.</>}
          active={active === "agy"}
          disabled={mutation.isPending || (!settings.agyAvailable && active !== "agy")}
          onPick={() => choose("agy")}
        />
      </div>

      <AgyStatus settings={settings} />

      <div className="grid grid-cols-1 gap-3 win-sm:grid-cols-2">
        <ModelField
          id="engine-claude-model"
          label="Default Claude model"
          hint="When an agent names none. Empty: the worker's default."
          value={settings.claudeModel}
          options={settings.claudeModels}
          saving={mutation.isPending}
          onSave={(claudeModel) => mutation.mutate({ claudeModel }, { onSuccess: () => toast.success("Default Claude model saved") })}
        />
        <ModelField
          id="engine-agy-model"
          label="Antigravity model"
          hint="Every agent turn on Antigravity. Empty: agy's own default."
          value={settings.agyModel}
          options={settings.agyModels}
          saving={mutation.isPending}
          onSave={(agyModel) => mutation.mutate({ agyModel }, { onSuccess: () => toast.success("Antigravity model saved") })}
        />
      </div>

      <p className="text-[12px] leading-relaxed text-muted-foreground">
        Only agents' conversations follow this. Pasted cards, WhatsApp imports and reply drafts stay on Claude Code: they read text
        strangers wrote and rely on its per-run tool allowlist, which agy has no equivalent of. On Antigravity an agent runs with every
        tool approved, its other MCP servers are not attached, and switching engine starts it a new session with its last few messages
        carried over.
      </p>
    </div>
  );
}

function EngineTile({
  icon,
  title,
  detail,
  active,
  disabled,
  onPick,
}: {
  icon: React.ReactNode;
  title: string;
  detail: React.ReactNode;
  active: boolean;
  disabled: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={disabled}
      aria-pressed={active}
      className={cn(
        "flex items-start gap-3 rounded-lg border p-3 text-left transition-colors",
        active ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border hover:bg-muted/40",
        disabled && !active && "cursor-not-allowed opacity-60 hover:bg-transparent",
      )}
    >
      <span
        className={cn(
          "grid size-8 shrink-0 place-items-center rounded-md border",
          active ? "border-primary/40 bg-primary/10 text-primary" : "border-border bg-muted/50 text-muted-foreground",
        )}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13.5px] font-medium">{title}</span>
        <span className="mt-0.5 block text-[12px] text-muted-foreground">{detail}</span>
      </span>
      {active ? <Check className="mt-0.5 size-4 shrink-0 text-primary" /> : null}
    </button>
  );
}

/** What the worker found on the host, and the fix when agy cannot be used. */
function AgyStatus({ settings }: { settings: EngineSettings }) {
  const checked = settings.agyCheckedAt ? `checked ${relativeTime(settings.agyCheckedAt)}` : null;
  if (settings.agyAvailable) {
    return (
      <p className="flex flex-wrap items-center gap-1.5 text-[12.5px] text-muted-foreground">
        <CheckCircle2 className="size-3.5 text-[var(--kind-done)]" />
        Antigravity {settings.agyVersion ?? ""} is signed in on the host
        <code className="font-mono text-[11.5px]">{settings.agyBin}</code>
        {settings.agyModels.length ? ` · ${settings.agyModels.length} models` : ""}
        {checked ? ` · ${checked}` : ""}
      </p>
    );
  }
  return (
    <div className="flex items-start gap-2 rounded-md border border-[var(--waiting)]/40 bg-[var(--waiting)]/10 px-3 py-2 text-[12.5px]">
      <AlertCircle className="mt-0.5 size-3.5 shrink-0 text-[var(--waiting)]" />
      <div className="min-w-0 space-y-0.5">
        <p>{settings.agyProblem ?? "Antigravity cannot be used right now."}</p>
        {settings.activeEngine === "agy" ? (
          <p className="text-muted-foreground">Antigravity is chosen, so agent turns will fail until this is fixed — or switch back to Claude Code.</p>
        ) : null}
        {!settings.agyInstalled && settings.agyCheckedAt ? (
          <p className="text-muted-foreground">
            Install it on the host with <code className="font-mono">curl -fsSL https://antigravity.google/cli/install.sh | bash</code>.
          </p>
        ) : null}
        {checked ? <p className="text-muted-foreground">{checked}</p> : null}
      </div>
    </div>
  );
}

function ModelField({
  id,
  label,
  hint,
  value,
  options,
  saving,
  onSave,
}: {
  id: string;
  label: string;
  hint: string;
  value: string;
  options: EngineModelOption[];
  saving: boolean;
  onSave: (value: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const dirty = draft.trim() !== value;
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="text-[12.5px] font-medium">
        {label}
      </label>
      <div className="flex gap-2">
        <Input
          id={id}
          list={`${id}-options`}
          value={draft}
          placeholder="default"
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && dirty) onSave(draft.trim());
          }}
          className="font-mono text-[12.5px]"
        />
        <datalist id={`${id}-options`}>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </datalist>
        {dirty ? (
          <Button size="sm" disabled={saving} onClick={() => onSave(draft.trim())}>
            Save
          </Button>
        ) : null}
      </div>
      <p className="text-[11.5px] text-muted-foreground">{hint}</p>
    </div>
  );
}
